import './index.css';
import { $isCodeHighlightNode } from '@lexical/code';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { getDOMRangeRect } from '../../utils/getDOMRangeRect';
import { getSelectedNode } from '../../utils/getSelectedNode';
import { setFloatingElemPosition } from '../../utils/setFloatingElemPosition';
import { INSERT_INLINE_COMMAND } from '../CommentPlugin';
import useAIAssistInvoker from '../../hooks/useAIAssistInvoker';
import AIAssistHomeScreen from '../../aiassist/ai-assist-home-screen';
import { useSettings } from '../../context/SettingsContext';
import { HIDE_AI_ASSIST_HOME, SHOW_AI_ASSIST_HOME } from '../../Editor';


function Divider(): JSX.Element {
  return <div className="floating-toolbar-divider"/>;
}

function TextFormatFloatingToolbar({
                                     editor,
                                     anchorElem,
                                     isLink,
                                     isBold,
                                     isItalic,
                                     isUnderline,
                                     isCode,
                                     isStrikethrough,
                                     isSubscript,
                                     isSuperscript
                                   }: {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
  isBold: boolean;
  isCode: boolean;
  isItalic: boolean;
  isLink: boolean;
  isStrikethrough: boolean;
  isSubscript: boolean;
  isSuperscript: boolean;
  isUnderline: boolean;
}): JSX.Element {
  const popupCharStylesEditorRef = useRef<HTMLDivElement | null>(null);
  const [self_close_floating_text, set_self_close_floating_text] = useState(false);
  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  const insertComment = () => {
    editor.dispatchCommand(INSERT_INLINE_COMMAND, undefined);
  };

  function mouseMoveListener(e: MouseEvent) {
    if (
      popupCharStylesEditorRef?.current &&
      (e.buttons === 1 || e.buttons === 3)
    ) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'none') {
        const x = e.clientX;
        const y = e.clientY;
        const elementUnderMouse = document.elementFromPoint(x, y);

        if (!popupCharStylesEditorRef.current.contains(elementUnderMouse)) {
          // Mouse is not over the target element => not a normal click, but probably a drag
          popupCharStylesEditorRef.current.style.pointerEvents = 'none';
        }
      }
    }
  }

  function mouseUpListener(e: MouseEvent) {
    if (popupCharStylesEditorRef?.current) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'auto') {
        popupCharStylesEditorRef.current.style.pointerEvents = 'auto';
      }
    }
  }

  useEffect(() => {
    if (popupCharStylesEditorRef?.current) {
      document.addEventListener('mousemove', mouseMoveListener);
      document.addEventListener('mouseup', mouseUpListener);

      return () => {
        document.removeEventListener('mousemove', mouseMoveListener);
        document.removeEventListener('mouseup', mouseUpListener);
      };
    }
  }, [popupCharStylesEditorRef]);

  const updateTextFormatFloatingToolbar = useCallback(() => {
    const selection = $getSelection();

    const popupCharStylesEditorElem = popupCharStylesEditorRef.current;
    const nativeSelection = window.getSelection();

    if (popupCharStylesEditorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();
    if (
      selection !== null &&
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      editor.dispatchCommand(HIDE_AI_ASSIST_HOME, undefined);
      const rangeRect = getDOMRangeRect(nativeSelection, rootElement);
      setFloatingElemPosition(rangeRect, popupCharStylesEditorElem, anchorElem);
    }
  }, [editor, anchorElem]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        updateTextFormatFloatingToolbar();
      });
    };

    window.addEventListener('resize', update);
    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);
      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [editor, updateTextFormatFloatingToolbar, anchorElem]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateTextFormatFloatingToolbar();
    });
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateTextFormatFloatingToolbar();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateTextFormatFloatingToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, updateTextFormatFloatingToolbar]);



  if (self_close_floating_text) {
    return (
      <>
      </>
    );
  }

  return (
    <div ref={popupCharStylesEditorRef} className="floating-text-format-popup">
      {editor.isEditable() && (
        <>
        {/* Ask cambri floting menu commented commenetd*/}
          <button
            onClick={() => {
              set_self_close_floating_text(true);
              editor.dispatchCommand(SHOW_AI_ASSIST_HOME, undefined);
            }}
            className={'ask-cambri flex items-center popup-item spaced ' + (isBold ? 'active' : '')}
            aria-label="Ask Cambri">

            <svg className='wand-icon' width="20" height="16" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M13.5005 7.5C13.5005 7.63261 13.4478 7.75979 13.354 7.85355C13.2603 7.94732 13.1331 8 13.0005 8H12.0005V9C12.0005 9.13261 11.9478 9.25979 11.854 9.35355C11.7603 9.44732 11.6331 9.5 11.5005 9.5C11.3679 9.5 11.2407 9.44732 11.1469 9.35355C11.0532 9.25979 11.0005 9.13261 11.0005 9V8H10.0005C9.86787 8 9.74069 7.94732 9.64692 7.85355C9.55315 7.75979 9.50048 7.63261 9.50048 7.5C9.50048 7.36739 9.55315 7.24021 9.64692 7.14645C9.74069 7.05268 9.86787 7 10.0005 7H11.0005V6C11.0005 5.86739 11.0532 5.74021 11.1469 5.64645C11.2407 5.55268 11.3679 5.5 11.5005 5.5C11.6331 5.5 11.7603 5.55268 11.854 5.64645C11.9478 5.74021 12.0005 5.86739 12.0005 6V7H13.0005C13.1331 7 13.2603 7.05268 13.354 7.14645C13.4478 7.24021 13.5005 7.36739 13.5005 7.5ZM1.50047 2.5H2.50047V3.5C2.50047 3.63261 2.55315 3.75979 2.64692 3.85355C2.74069 3.94732 2.86787 4 3.00047 4C3.13308 4 3.26026 3.94732 3.35403 3.85355C3.4478 3.75979 3.50047 3.63261 3.50047 3.5V2.5H4.50047C4.63308 2.5 4.76026 2.44732 4.85403 2.35355C4.9478 2.25979 5.00047 2.13261 5.00047 2C5.00047 1.86739 4.9478 1.74021 4.85403 1.64645C4.76026 1.55268 4.63308 1.5 4.50047 1.5H3.50047V0.5C3.50047 0.367392 3.4478 0.240215 3.35403 0.146447C3.26026 0.0526785 3.13308 0 3.00047 0C2.86787 0 2.74069 0.0526785 2.64692 0.146447C2.55315 0.240215 2.50047 0.367392 2.50047 0.5V1.5H1.50047C1.36787 1.5 1.24069 1.55268 1.14692 1.64645C1.05315 1.74021 1.00047 1.86739 1.00047 2C1.00047 2.13261 1.05315 2.25979 1.14692 2.35355C1.24069 2.44732 1.36787 2.5 1.50047 2.5ZM9.50048 10H9.00048V9.5C9.00048 9.36739 8.9478 9.24021 8.85403 9.14645C8.76026 9.05268 8.63308 9 8.50047 9C8.36787 9 8.24069 9.05268 8.14692 9.14645C8.05315 9.24021 8.00047 9.36739 8.00047 9.5V10H7.50047C7.36787 10 7.24069 10.0527 7.14692 10.1464C7.05315 10.2402 7.00047 10.3674 7.00047 10.5C7.00047 10.6326 7.05315 10.7598 7.14692 10.8536C7.24069 10.9473 7.36787 11 7.50047 11H8.00047V11.5C8.00047 11.6326 8.05315 11.7598 8.14692 11.8536C8.24069 11.9473 8.36787 12 8.50047 12C8.63308 12 8.76026 11.9473 8.85403 11.8536C8.9478 11.7598 9.00048 11.6326 9.00048 11.5V11H9.50048C9.63308 11 9.76026 10.9473 9.85403 10.8536C9.9478 10.7598 10.0005 10.6326 10.0005 10.5C10.0005 10.3674 9.9478 10.2402 9.85403 10.1464C9.76026 10.0527 9.63308 10 9.50048 10ZM11.7074 3L3.00047 11.7069C2.81296 11.8943 2.5587 11.9995 2.2936 11.9995C2.0285 11.9995 1.77424 11.8943 1.58672 11.7069L0.292974 10.4144C0.200091 10.3215 0.126411 10.2113 0.0761423 10.0899C0.0258733 9.96858 0 9.83853 0 9.70719C0 9.57585 0.0258733 9.44579 0.0761423 9.32445C0.126411 9.20311 0.200091 9.09286 0.292974 9L9.00048 0.293125C9.09334 0.200242 9.20359 0.126562 9.32493 0.0762935C9.44627 0.0260245 9.57632 0.000151157 9.70766 0.000151157C9.839 0.000151157 9.96906 0.0260245 10.0904 0.0762935C10.2117 0.126562 10.322 0.200242 10.4149 0.293125L11.7074 1.58562C11.8002 1.67849 11.8739 1.78874 11.9242 1.91008C11.9745 2.03142 12.0003 2.16147 12.0003 2.29281C12.0003 2.42415 11.9745 2.55421 11.9242 2.67555C11.8739 2.79689 11.8002 2.90714 11.7074 3ZM8.29297 5L7.00047 3.70687L1.00047 9.70687L2.29297 11L8.29297 5ZM11.0005 2.29313L9.70735 1L7.70735 3L9.00048 4.29313L11.0005 2.29313Z"
                fill="#A033FF"/>
            </svg>

            <span className='float-text'>Ask Cambri</span>

          </button>

          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
            }}
            className={'popup-item spaced ' + (isBold ? 'active' : '')}
            aria-label="Format text as bold">
            <i className="format bold"/>
          </button>

          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
            }}
            className={'popup-item spaced ' + (isItalic ? 'active' : '')}
            aria-label="Format text as italics">
            <i className="format italic"/>
          </button>

          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
            }}
            className={'popup-item spaced ' + (isUnderline ? 'active' : '')}
            aria-label="Format text to underlined">
            <i className="format underline"/>
          </button>

          <Divider/>

          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
            }}
            className={'popup-item spaced ' + (isStrikethrough ? 'active' : '')}
            aria-label="Format text with a strikethrough">
            <i className="format strikethrough"/>
          </button>

          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');
            }}
            className={'popup-item spaced ' + (isSubscript ? 'active' : '')}
            title="Subscript"
            aria-label="Format Subscript">
            <i className="format subscript"/>
          </button>

          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');
            }}
            className={'popup-item spaced ' + (isSuperscript ? 'active' : '')}
            title="Superscript"
            aria-label="Format Superscript">
            <i className="format superscript"/>
          </button>

          <Divider/>

          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
            }}
            className={'popup-item spaced ' + (isCode ? 'active' : '')}
            aria-label="Insert code block">
            <i className="format code"/>
          </button>
          <button
            onClick={insertLink}
            className={'popup-item spaced ' + (isLink ? 'active' : '')}
            aria-label="Insert link">
            <i className="format link"/>
          </button>
        </>
      )}
      {/*Insert button commenetd*/}
      {/* <button
        onClick={insertComment}
        className={'popup-item spaced insert-comment'}
        aria-label="Insert comment">
        <i className="format add-comment"/>
      </button> */}
    </div>
  );
}

function useFloatingTextFormatToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement
): JSX.Element | null {
  const [isText, setIsText] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isCode, setIsCode] = useState(false);

  const updatePopup = useCallback(() => {
    editor.getEditorState().read(() => {
      // Should not to pop up the floating toolbar when using IME input
      if (editor.isComposing()) {
        return;
      }
      const selection = $getSelection();
      const nativeSelection = window.getSelection();
      const rootElement = editor.getRootElement();

      if (
        nativeSelection !== null &&
        (!$isRangeSelection(selection) ||
          rootElement === null ||
          !rootElement.contains(nativeSelection.anchorNode))
      ) {
        setIsText(false);
        return;
      }

      if (!$isRangeSelection(selection)) {
        return;
      }

      const node = getSelectedNode(selection);

      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsSubscript(selection.hasFormat('subscript'));
      setIsSuperscript(selection.hasFormat('superscript'));
      setIsCode(selection.hasFormat('code'));

      // Update links
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      if (
        !$isCodeHighlightNode(selection.anchor.getNode()) &&
        selection.getTextContent() !== ''
      ) {
        setIsText($isTextNode(node));
      } else {
        setIsText(false);
      }

      const rawTextContent = selection.getTextContent().replace(/\n/g, '');
      if (!selection.isCollapsed() && rawTextContent === '') {
        setIsText(false);
        return;
      }
    });
  }, [editor]);

  useEffect(() => {
    document.addEventListener('selectionchange', updatePopup);
    return () => {
      document.removeEventListener('selectionchange', updatePopup);
    };
  }, [updatePopup]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updatePopup();
      }),
      editor.registerRootListener(() => {
        if (editor.getRootElement() === null) {
          setIsText(false);
        }
      })
    );
  }, [editor, updatePopup]);

  if (!isText || isLink) {
    return null;
  }

  return createPortal(
    <TextFormatFloatingToolbar
      editor={editor}
      anchorElem={anchorElem}
      isLink={isLink}
      isBold={isBold}
      isItalic={isItalic}
      isStrikethrough={isStrikethrough}
      isSubscript={isSubscript}
      isSuperscript={isSuperscript}
      isUnderline={isUnderline}
      isCode={isCode}
    />,
    anchorElem
  );
}

export default function FloatingTextFormatToolbarPlugin({
                                                          anchorElem = document.body
                                                        }: {
  anchorElem?: HTMLElement;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  return useFloatingTextFormatToolbar(editor, anchorElem);
}
